// ─── How it works — Tổng quan + Kiến trúc + Phân quyền + Các trang ───

import { SectionTitle, Card } from './shared';
import ArchitectureDiagram from './ArchitectureDiagram';

export function OverviewSection() {
  return (
    <section className="space-y-5">
      <SectionTitle
        id="overview"
        icon="ri-compass-3-line"
        iconBg="bg-primary-500/10"
        iconColor="text-primary-400"
        kicker="Tổng quan"
        title="Chín Hồng Mao là gì?"
      />

      <Card>
        <p className="text-foreground-700 text-sm leading-relaxed">
          <strong className="text-foreground-950">Chín Hồng Mao</strong> là nền tảng giao dịch tự động
          (automated trading bot) kết nối trực tiếp vào tài khoản <strong>cTrader (Spotware)</strong> qua
          Open API. Nó không phải là một EA chạy trên MT4/MT5, mà là một <strong>web app độc lập</strong> hoạt
          động như một cBot, giúp bạn theo dõi thị trường theo từng tick, phân tích chỉ báo kỹ thuật, rồi tự
          động gửi tín hiệu và vào lệnh theo logic đã cấu hình.
        </p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { icon: 'ri-radar-line', color: 'text-accent-400', title: 'Theo dõi realtime', desc: 'Nhận giá bid/ask theo từng tick và nến (trendbar) trực tiếp từ cTrader qua kết nối WebSocket + SSE.' },
          { icon: 'ri-line-chart-line', color: 'text-primary-400', title: 'Phân tích chỉ báo', desc: 'Tính SMA, HMA, TMA, Bollinger Bands trên buffer nến 100+ để phát hiện tín hiệu giao dịch.' },
          { icon: 'ri-send-plane-line', color: 'text-secondary-400', title: 'Gửi tín hiệu Telegram', desc: 'Tự động bắn thông báo có cấu trúc (Status + Note + chi tiết) tới kênh Telegram khi có tín hiệu.' },
          { icon: 'ri-play-circle-line', color: 'text-emerald-400', title: 'Tự động vào lệnh', desc: 'Vào lệnh MARKET, dời Stop Loss theo lợi nhuận, và copy trade từ cTrader sang MT4 theo thời gian thực.' },
        ].map((f) => (
          <Card key={f.title} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
              <i className={`${f.icon} ${f.color} text-base w-5 h-5 flex items-center justify-center`}></i>
            </div>
            <div>
              <p className="text-foreground-900 text-sm font-semibold">{f.title}</p>
              <p className="text-foreground-600 text-xs mt-1 leading-relaxed">{f.desc}</p>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

export function ArchitectureSection() {
  const layers = [
    {
      icon: 'ri-window-2-line',
      color: 'text-primary-400',
      title: 'Frontend — React SPA',
      desc: 'Giao diện được xây bằng React + TypeScript + TailwindCSS. Toàn bộ logic engine chạy ngay trong trình duyệt, giúp tín hiệu được xử lý tức thì mà không cần server trung gian mỗi tick.',
    },
    {
      icon: 'ri-database-2-line',
      color: 'text-accent-400',
      title: 'Backend — Supabase',
      desc: 'Xử lý đăng nhập (Auth), lưu dữ liệu (Database) và chạy các hàm serverless (Edge Functions). Toàn bộ cấu hình strategy, workspace và trạng thái bot đều được đồng bộ lên đây.',
    },
    {
      icon: 'ri-plug-line',
      color: 'text-secondary-400',
      title: 'cTrader Gateway — Edge Function',
      desc: 'Là cầu nối duy nhất tới cTrader Open API. Nó mở WebSocket tới Spotware, xác thực token, rồi trả dữ liệu về dưới dạng SSE stream cho frontend, hoặc nhận lệnh đặt/đóng/sửa SLTP.',
    },
    {
      icon: 'ri-telegram-line',
      color: 'text-emerald-400',
      title: 'Telegram Alert — Edge Function',
      desc: 'Nhận nội dung thông báo từ engine, đọc cấu hình bot token/chat id từ database, rồi gửi qua Telegram API. Có cơ chế chống trùng lặp (dedup) để không bắn trùng tin nhắn.',
    },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="architecture"
        icon="ri-stack-line"
        iconBg="bg-secondary-500/10"
        iconColor="text-secondary-400"
        kicker="Kiến trúc"
        title="Hệ thống hoạt động như thế nào"
        subtitle="Ứng dụng chia làm 4 tầng chính, mỗi tầng đảm nhận một vai trò riêng biệt nhưng liên kết chặt chẽ với nhau."
      />

      <div className="space-y-3">
        {layers.map((l) => (
          <Card key={l.title} className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
              <i className={`${l.icon} ${l.color} text-lg w-5 h-5 flex items-center justify-center`}></i>
            </div>
            <div>
              <p className="text-foreground-900 text-sm font-semibold">{l.title}</p>
              <p className="text-foreground-600 text-xs mt-1 leading-relaxed">{l.desc}</p>
            </div>
          </Card>
        ))}
      </div>

      <ArchitectureDiagram />
    </section>
  );
}

export function RolesSection() {
  const roles = [
    {
      icon: 'ri-vip-crown-line',
      color: 'text-accent-400',
      name: 'Super Admin',
      email: 'admin@chin.vn',
      desc: 'Tài khoản duy nhất của hệ thống, toàn quyền. Cấu hình cTrader connection, Telegram bot + MT4 master, strategy và các công cụ debug.',
    },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="roles"
        icon="ri-shield-keyhole-line"
        iconBg="bg-accent-500/10"
        iconColor="text-accent-400"
        kicker="Phân quyền"
        title="Một tài khoản duy nhất"
        subtitle="Hệ thống chỉ có duy nhất một tài khoản Super Admin. Không có đăng ký mới và không có vai trò user/admin nào khác."
      />

      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 max-w-xl">
        {roles.map((r) => (
          <Card key={r.name} className="flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-background-200/60 flex items-center justify-center">
                <i className={`${r.icon} ${r.color} text-sm w-4 h-4 flex items-center justify-center`}></i>
              </div>
              <div>
                <p className="text-foreground-900 text-sm font-semibold">{r.name}</p>
                {r.email && <p className="text-foreground-400 text-[10px] font-mono">{r.email}</p>}
              </div>
            </div>
            <p className="text-foreground-600 text-xs leading-relaxed">{r.desc}</p>
          </Card>
        ))}
      </div>
    </section>
  );
}

export function PagesSection() {
  const pages = [
    { path: '/login', icon: 'ri-lock-2-line', name: 'Login', desc: 'Đăng nhập bằng tài khoản Super Admin. Hỗ trợ quên mật khẩu.' },
    { path: '/dashboard', icon: 'ri-dashboard-line', name: 'Dashboard', desc: 'Cấu hình cTrader connection, Telegram bot và MT4 master accounts để nhận copy trade.' },
    { path: '/strategies', icon: 'ri-brain-line', name: 'Strategies', desc: 'Grid các strategy + sidebar symbol lists và copy targets. Kèm popup debug: Accounts, Market, Orders, Copy Trade.' },
    { path: '/strategies/:id', icon: 'ri-settings-3-line', name: 'Strategy Detail', desc: 'Cấu hình indicator + entry/exit rules + risk. Với strategy chin/trailingStop hiển thị màn monitor riêng.' },
    { path: '/accounts', icon: 'ri-user-settings-line', name: 'Accounts', desc: 'Đổi mật khẩu và xem thông tin tài khoản Super Admin.' },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="pages"
        icon="ri-layout-grid-line"
        iconBg="bg-primary-500/10"
        iconColor="text-primary-400"
        kicker="Giao diện"
        title="Các trang chính"
        subtitle="Thanh điều hướng bên trái hiển thị đầy đủ các trang cho tài khoản Super Admin duy nhất."
      />

      <div className="space-y-3">
        {pages.map((p) => (
          <Card key={p.path} className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
              <i className={`${p.icon} text-primary-400 text-base w-5 h-5 flex items-center justify-center`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-foreground-900 text-sm font-semibold">{p.name}</span>
                <code className="text-foreground-400 text-[10px] font-mono bg-background-200/60 px-1.5 py-0.5 rounded">{p.path}</code>
              </div>
              <p className="text-foreground-600 text-xs mt-1 leading-relaxed">{p.desc}</p>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}